import type { IMedia } from '../../lib/MediasLib';
import type { AllowChangeGrid } from '../../services/modules/product-colors-packs/product-colors-packs.types';
import type { Classification } from '../classifications/classification.types';
import type { Collection } from '../collections/collections.types';
import { OrderItemPriceComposition } from '../orders/order.types';
import type { TagType } from '../tag/tag.types';

export type WhenOutOfStockType = 'HIDE' | 'SHOW_UNAVAILABLE' | 'CONTINUE_SELLING';

export enum AvailabilityEnum {
  READY_FOR_DELIVERY = 'READY_FOR_DELIVERY',
  EMPTY = 'EMPTY',
  FUTURE_STOCK = 'FUTURE_STOCK',
  INFINITE_PRE_ORDER = 'INFINITE_PRE_ORDER',
  MATERIAL_PRE_ORDER = 'MATERIAL_PRE_ORDER',
}

/**
 * Espelho do enum binário `CompactAvailability` da API.
 * `PRE_ORDER` agrupa os subtipos INFINITE_PRE_ORDER, MATERIAL_PRE_ORDER e FUTURE_STOCK.
 */
export enum CompactAvailability {
  PRE_ORDER = 'PRE_ORDER',
  READY_FOR_DELIVERY = 'READY_FOR_DELIVERY',
}

export type ViewMode = 'GRID' | 'LIST';

export type PriceValues = {
  promotionalPrice?: number;
  retailPrice?: number;
  wholesalePrice: number;
};

export interface Tag {
  id: string;
  name: string;
  fontColor: string;
  backgroundColor: string;
  opacity: number;
  tagType: TagType;
}

export interface ProductVariations {
  hasManyColors: boolean;
  hasManySizes: boolean;
  colors: (ProductColor & { productColorId: string })[];
  sizes: ProductSize[];
}

export interface DeliveryWindow {
  id: string;
  code?: string;
  name: string;
  startDate: string | Date;
  endDate: string | Date;
  preOrderLimit?: string | Date;
}

export interface IProductColor {
  id: string;
  colorId: string;
  code: string;
  name: string;
  isImage: boolean;
  hexcode?: string;
  imageUrl?: string;
  customized: boolean;
  allowChangePreOrderGrid?: AllowChangeGrid;
  allowChangeImmediateGrid?: AllowChangeGrid;
  classifications: Classification[];
  referenceColors: ReferenceColor[];
}

export interface ProductColor extends IProductColor {
  active: boolean;
  unique: boolean;
}

export interface ProductMedia extends IMedia {
  processed?: boolean;
  errorMessage?: string;
}

export interface ProductSize {
  id: string;
  position: number;
  name: string;
  unique: boolean;
  productSizeId?: string;
  active: boolean;
}

export interface Product {
  id: string;
  wholesalePrice: number;
  retailPrice: number;
}

export interface GetProductOutput {
  id: string;
  code: string;
  name: string;
  active: boolean;
  wholesalePrice: number;
  retailPrice: number;
  description: string;
  resume: string;
  collectionId: string;
  collection: Collection;
  integrationCode: string;
  availableForPreOrder: boolean;
  preOrderMinimumProduct: number | null;
  preOrderMinimumItem: number | null;
  readyForDeliveryMinimumProduct: number | null;
  readyForDeliveryMinimumItem: number | null;
  quantitySkus: number;
  preOrderMultipleProduct: number | null;
  preOrderMultipleItem: number | null;
  readyForDeliveryMultipleProduct: number | null;
  readyForDeliveryMultipleItem: number | null;
  referenceCode?: string;
}

export interface GetSimpleProductDTO {
  id: string;
  code: string;
  name: string;
  integrationCode: string;
}

export interface ProductPageDTO {
  active: boolean;
  code: string;
  collection: Collection;
  id: string;
  media: string;
  name: string;
  preOrderMinimumItem: number;
  preOrderMinimumProduct: number;
  preOrderMultipleItem: number;
  preOrderMultipleProduct: number;
  readyForDeliveryMinimumItem: number;
  readyForDeliveryMinimumProduct: number;
  readyForDeliveryMultipleItem: number;
  readyForDeliveryMultipleProduct: number;
  referenceCode?: string;
  retailPrice: number;
  wholesalePrice: number;
}

export interface ESProductSearch {
  active: boolean;
  allColors: ESProductColor[];
  availability: AvailabilityEnum[];
  brandId: string;
  catalogs: ESProductCatalog[];
  category: ESProductCategory;
  code: string;
  collection: ESProductCollection;
  color: ESProductColor;
  colorClassifications: ESColorClassification[];
  createdAt: Date;
  deliveryWindows: ESProductDeliveryWindow[];
  grid: ESProductGrid[];
  medias: ESProductMedia[];
  name: string;
  preOrderMinimumItem?: number;
  preOrderMinimumProduct?: number;
  prices: ESProductPrice[];
  productColorId: string;
  productId: string;
  promotions: ESProductPromotion[];
  referenceCode?: string;
  description?: string;
  resume?: string;
  resumeInfo: ESProductResumeInfo;
  tags: ESProductTag[];
  updatedAt: Date;
}

export interface ESProductCatalog {
  catalogId: string;
  catalogName: string;

  catalogSectionId: string;
  catalogSectionName: string;
  catalogSectionPosition: number;

  catalogSectionProductId: string; // useListActions
  catalogSectionProductPosition: number;
}

export interface ESProductGrid {
  availability: AvailabilityEnum;
  deliveryWindow: ESProductDeliveryWindow;
  items: ESProductGridItem[];
  show: boolean;
}

interface ESSku {
  id: string;
}

interface ESProductGridItem {
  color: ESProductColor;
  size: ESProductSize;
  availableForSale: boolean;
  availableForReadyForDeliveryWithoutStock: boolean;
  prices: ESProductPrice[];
  stock: ESProductStock;
  sku: ESSku;
}

interface ESProductStock {
  id: string;
  availableAmount: number;
  startDate: Date;
  endDate: Date;
  availableForSale: boolean;
}

export interface ESProductPrice {
  skuId: string;
  priceTableId: string;
  priceTableSkuId: string;
  priceTableName: string;
  wholesalePrice: number;
  retailPrice: number;
  sizeId: string;
  productSizeId: string;
  productSizeName: string;
  productSizePosition: number;
  productSizeUnique: boolean;
  promotion: ESProductPromotion;
  promotionalPrice?: number;
  priceComposition?: OrderItemPriceComposition;
}

interface ESProductPromotion {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  active: boolean;
  tag: ESProductTag;
}

interface ESProductDeliveryWindow {
  id: string;
  code: string;
  name: string;
  startDate: Date;
  endDate: Date;
  preOrderLimit: Date;
}

interface ESProductResumeInfo {
  stock: number;
  prices: ESProductResumePriceInfo[];
}

interface ESProductResumePriceInfo {
  priceTableId: string;
  minPrice: number;
  maxPrice: number;
  promotionalPrice: number;
}

export interface ESProductTag {
  id: string;
  name: string;
  fontColor: string;
  backgroundColor: string;
  opacity: number;
}

interface ESProductCategory {
  id: string;
  name: string;
  classification: string;
  parent: ESProductCategory;
}

interface ESProductCollection {
  id: string;
  name: string;
  code: string;
}

export type ESProductSize = {
  id: string;
  productSizeId: string;
  position: number;
  name: string;
  unique: boolean;
  available: boolean;
};

export interface ESProductColor {
  id: string;
  productColorId: string;
  code: string;
  name: string;
  isImage: boolean;
  hexcode?: string;
  imageUrl?: string;
  referenceColor: { name: string; hexcode: string }[];
  allowChangePreOrderGrid?: AllowChangeGrid;
  allowChangeImmediateGrid?: AllowChangeGrid;
}

export interface SizeDTO {
  id: string;
  name: string;
  position: number;
  unique: boolean;
}

export interface ESProductMedia {
  id: string;
  position: number;
  type: 'IMAGE' | 'VIDEO';
  url: string;
  processed: boolean;
  errorMessage?: string;
}

export interface ReferenceColor {
  id: string;
  name: string;
  hexcode: string;
}

export interface ESColorClassificationType {
  id: string;
  name: string;
  useAsTag: boolean;
  useAsFilter: boolean;
}

export interface ESColorClassification {
  id: string;
  name: string;
  code: string;
  description?: string;

  classificationType: ESColorClassificationType;
}

export interface ProductColorPageDTO {
  id: string;

  name: string;

  code: string;

  imageUrl: string;

  productId: string;

  productName: string;

  productCode: string;
}
